import React, { useRef, useState } from "react";
import { TextField, Button } from "@mui/material";
import { UploadCloud, ImagePlus, X, ArrowLeft, ArrowRight, Trash2 } from "lucide-react";
import { useAuth } from "../../services/AuthContext.js";
import {
  getPresignedUploadUrls,
  uploadImageToS3,
  createPost,
} from "../../services/user_posts.js";
import "./create_post.css";

const MAX_IMAGES = 5;

function CreatePost({ onCreate }) {
  const { getIdToken } = useAuth();
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]); // { id, url, file }
  const [modalOpen, setModalOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const openFileDialog = () => inputRef.current?.click();

  const addFiles = (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;

    setImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      const toAdd = files.slice(0, remaining).map((f) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        url: URL.createObjectURL(f),
        file: f,
      }));
      return [...prev, ...toAdd];
    });
    setError("");
    setModalOpen(true);
  };

  const handleInputChange = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const removeImage = (id) => {
    setImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.url);
      return prev.filter((i) => i.id !== id);
    });
  };

  const moveImage = (from, to) => {
    setImages((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const onThumbDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return;
    moveImage(dragIndex, index);
    setDragIndex(null);
  };

  const closeAndReset = (revoke) => {
    if (revoke) images.forEach((i) => URL.revokeObjectURL(i.url));
    setImages([]);
    setTitle("");
    setDescription("");
    setError("");
    setDragIndex(null);
    setModalOpen(false);
  };

  const canSave = images.length > 0 && title.trim() && description.trim() && !saving;

  const handleSave = async () => {
    if (images.length === 0) return setError("Adicione pelo menos uma imagem.");
    if (!title.trim()) return setError("O título é obrigatório.");
    if (!description.trim()) return setError("A descrição é obrigatória.");

    try {
      setSaving(true);
      setError("");
      const token = await getIdToken();

      // 1. Request presigned upload URLs (order matches the arranged images).
      const targets = await getPresignedUploadUrls(
        token,
        images.map((i) => ({ file_type: i.file.type }))
      );

      if (targets.length !== images.length) {
        throw new Error("Falha ao preparar o envio das imagens.");
      }

      // 2. Upload each image to S3.
      await Promise.all(images.map((img, idx) => uploadImageToS3(targets[idx].upload_url, img.file)));

      // 3. Create the post with the resulting image keys (kept in order).
      const post = await createPost(token, {
        title: title.trim(),
        description: description.trim(),
        images: targets.map((t) => t.image_key),
      });

      onCreate(post);
      closeAndReset(true);
    } catch (err) {
      console.error("Erro ao criar publicação:", err);
      setError(err.message || "Não foi possível criar a publicação.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="create-post">
      <div
        className={`create-post-dropzone ${dragActive ? "active" : ""}`}
        onClick={openFileDialog}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={0}
      >
        <UploadCloud size={34} className="create-post-drop-icon" />
        <p className="create-post-drop-title">Criar nova publicação</p>
        <p className="create-post-drop-hint">
          Arraste imagens aqui ou clique para selecionar (até {MAX_IMAGES})
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleInputChange}
        />
      </div>

      {modalOpen && (
        <div className="create-post-overlay">
          <div className="create-post-modal">
            <div className="create-post-modal-header">
              <h3>Nova publicação</h3>
              <button
                type="button"
                className="create-post-close"
                onClick={() => closeAndReset(true)}
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>

            <div className="create-post-modal-body">
              <span className="create-post-label">
                Imagens — arraste para reordenar ({images.length}/{MAX_IMAGES})
              </span>

              <div className="create-post-thumbs">
                {images.map((img, index) => (
                  <div
                    key={img.id}
                    className={`create-post-thumb ${dragIndex === index ? "dragging" : ""}`}
                    draggable
                    onDragStart={undefined}
                    onDragStartCapture={() => setDragIndex(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onThumbDrop(index)}
                    onDragEnd={() => setDragIndex(null)}
                  >
                    <img src={img.url} alt={`Imagem ${index + 1}`} className="create-post-thumb-img" />
                    <span className="create-post-thumb-order">{index + 1}</span>
                    <div className="create-post-thumb-actions">
                      <button
                        type="button"
                        onClick={() => moveImage(index, index - 1)}
                        disabled={index === 0}
                        aria-label="Mover para a esquerda"
                      >
                        <ArrowLeft size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(img.id)}
                        aria-label="Remover imagem"
                        className="create-post-thumb-remove"
                      >
                        <Trash2 size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveImage(index, index + 1)}
                        disabled={index === images.length - 1}
                        aria-label="Mover para a direita"
                      >
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}

                {images.length < MAX_IMAGES && (
                  <button type="button" className="create-post-add-more" onClick={openFileDialog}>
                    <ImagePlus size={22} />
                    <span>Adicionar</span>
                  </button>
                )}
              </div>

              <TextField
                label="Título"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                size="small"
                margin="dense"
                required
              />
              <TextField
                label="Descrição"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                size="small"
                margin="dense"
                required
              />

              {error && <div className="create-post-error">{error}</div>}
            </div>

            <div className="create-post-modal-footer">
              <Button
                className="create-post-cancel-btn"
                onClick={() => closeAndReset(true)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                className="create-post-save-btn"
                onClick={handleSave}
                disabled={!canSave}
              >
                {saving ? "Salvando..." : "Salvar publicação"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreatePost;
